import { useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import Footer from '@/pages/home/components/Footer';
import PageFade from '@/components/feature/PageFade';
import { readHomeReturn, saveHomeReturn } from '@/utils/homeReturnStore';

function BackButton({ onClick, label = 'Back' }: { onClick: () => void; label?: string }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="inline-flex items-center justify-center gap-2 h-[48px] px-4 text-[14px] font-medium text-solar-ink hover:text-solar-green transition-colors cursor-pointer whitespace-nowrap bg-transparent border-0 focus:outline-none focus-visible:ring-2 focus-visible:ring-solar-green focus-visible:ring-offset-2 rounded-md"
    >
      <i className="ri-arrow-left-line"></i>
      {label}
    </button>
  );
}

function EstimateButton({ onClick, children }: { onClick: () => void; children: React.ReactNode }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="inline-flex items-center justify-center h-[48px] px-6 rounded-[10px] bg-solar-green text-solar-ink text-[14px] font-semibold whitespace-nowrap hover:bg-[#00c850] transition-colors cursor-pointer border-0"
    >
      {children}
    </button>
  );
}

export default function Residential() {
  const navigate = useNavigate();

  // ONE return implementation: deterministic navigate to '/'. The homepage
  // will read sessionStorage and restore either the saved scrollY (came from
  // homepage) or scroll to #solutions (direct entry fallback).
  const goBack = useCallback(() => {
    const saved = readHomeReturn();
    if (!saved) {
      // Direct-entry fallback: mark intent to land on Solutions.
      saveHomeReturn(0, 'solutions');
    }
    navigate('/');
  }, [navigate]);

  // "Get My Estimate" also needs to go home; we mark the estimate section
  // as the return target so homepage lands there instead of the previous
  // scroll position.
  const goEstimate = useCallback(() => {
    saveHomeReturn(0, 'estimate');
    navigate('/');
  }, [navigate]);

  return (
    <PageFade>
    <div className="min-h-screen bg-solar-bg text-solar-ink">
      {/* Detail page header */}
      <div className="sticky top-0 z-10 bg-solar-bg/95 backdrop-blur-md border-b border-solar-divider">
        <div className="container-x flex items-center justify-between h-16">
          <BackButton onClick={goBack} />
          <p className="text-xs font-medium text-solar-green tracking-wider uppercase">Residential Solar</p>
        </div>
      </div>

      <main>
        {/* Intro */}
        <section className="pt-[56px] md:pt-[72px] pb-[56px] md:pb-[72px] bg-solar-bg">
          <div className="container-x">
            <div className="grid grid-cols-12 gap-8 md:gap-10 items-center">
              <div className="col-span-12 md:col-span-6">
                <h1 className="display-font text-[32px] md:text-[48px] lg:text-[54px] font-semibold leading-[1.05] text-solar-ink tracking-tight">
                  Solar for your home.
                </h1>
                <p className="mt-5 max-w-[460px] text-solar-muted text-[15px] md:text-[16px] leading-relaxed">
                  A complete residential energy solution combining solar, storage, and smarter energy use.
                </p>
                <div className="mt-8 flex flex-col sm:flex-row items-stretch sm:items-center gap-4">
                  <EstimateButton onClick={goEstimate}>Get My Estimate</EstimateButton>
                </div>
              </div>
              <div className="col-span-12 md:col-span-6">
                <div className="aspect-[4/3] w-full overflow-hidden rounded-2xl bg-solar-divider">
                  <img
                    src="https://readdy.ai/api/search-image?query=modern%20contemporary%20two%20story%20family%20home%20exterior%20with%20cleanly%20integrated%20black%20rooftop%20solar%20panels%2C%20warm%20wood%20siding%20and%20white%20stucco%2C%20soft%20green%20lawn%20and%20mature%20trees%2C%20late%20morning%20natural%20daylight%2C%20clear%20blue%20sky%2C%20editorial%20architecture%20photography%2C%20calm%20quiet%20premium%20feeling%2C%20cool%20neutral%20tones&width=1000&height=750&seq=res-intro-01&orientation=landscape"
                    alt="Modern residential home with integrated solar"
                    loading="lazy"
                    decoding="async"
                    className="w-full h-full object-cover object-top"
                  />
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Three parts */}
        <section className="py-[72px] md:py-[88px] bg-white">
          <div className="container-x">
            <div className="mb-12">
              <p className="text-xs font-medium text-solar-green tracking-wider uppercase">The system</p>
              <h2 className="display-font mt-3 text-[28px] md:text-[36px] font-semibold leading-[1.1] text-solar-ink">
                One system. Three parts.
              </h2>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8 md:gap-6">
              {[
                { n: '01', icon: 'ri-sun-line', title: 'Solar Panels', copy: 'Generate clean energy from your roof.' },
                { n: '02', icon: 'ri-battery-2-charge-line', title: 'Battery Storage', copy: 'Keep more of the energy you produce.' },
                { n: '03', icon: 'ri-cpu-line', title: 'Smart Energy', copy: 'Understand and manage how your home uses power.' },
              ].map((p) => (
                <div key={p.n} className="pt-5 border-t border-solar-ink/90">
                  <div className="flex items-center justify-between">
                    <span className="text-xs font-semibold text-solar-ink tracking-wider">{p.n}</span>
                    <i className={`${p.icon} text-solar-ink text-lg`}></i>
                  </div>
                  <h3 className="display-font mt-5 text-[20px] md:text-[22px] font-semibold text-solar-ink leading-tight">{p.title}</h3>
                  <p className="mt-2 text-solar-muted text-[14px] leading-relaxed max-w-[300px]">{p.copy}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Outcome */}
        <section className="py-[72px] md:py-[96px] bg-solar-bg">
          <div className="container-x">
            <div className="grid grid-cols-12 gap-8 md:gap-10 items-center">
              <div className="col-span-12 md:col-span-6">
                <p className="text-xs font-medium text-solar-green tracking-wider uppercase">Outcome</p>
                <h2 className="display-font mt-3 text-[28px] md:text-[36px] font-semibold leading-[1.1] text-solar-ink">
                  Made for everyday energy.
                </h2>
                <ul className="mt-8 space-y-4">
                  {['Lower grid dependence.', 'More predictable energy costs.', 'Backup when you need it.'].map((p) => (
                    <li key={p} className="flex items-start gap-3 text-solar-ink text-[15px] md:text-[16px] leading-relaxed">
                      <i className="ri-check-line text-solar-green text-lg mt-0.5"></i>
                      <span>{p}</span>
                    </li>
                  ))}
                </ul>
                <div className="mt-10 flex flex-col sm:flex-row items-stretch sm:items-center gap-4">
                  <EstimateButton onClick={goEstimate}>Get My Estimate</EstimateButton>
                </div>
              </div>
              <div className="col-span-12 md:col-span-6">
                <div className="aspect-[4/3] w-full overflow-hidden rounded-2xl bg-solar-divider">
                  <img
                    src="https://readdy.ai/api/search-image?query=modern%20suburban%20family%20home%20exterior%20with%20cleanly%20integrated%20black%20rooftop%20solar%20panels%2C%20white%20siding%20and%20warm%20wood%20accents%2C%20soft%20green%20landscaping%2C%20warm%20late%20afternoon%20natural%20daylight%2C%20clear%20sky%2C%20editorial%20architecture%20photography%2C%20quiet%20premium%20calm%20composition%2C%20cool%20neutral%20tones&width=1000&height=750&seq=res-outcome-01&orientation=landscape"
                    alt="Residential solar home"
                    loading="lazy"
                    decoding="async"
                    className="w-full h-full object-cover object-top"
                  />
                </div>
              </div>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
    </PageFade>
  );
}